import React, { useState } from 'react';
import { Bell, CheckCircle, XCircle, AlertCircle, Info, Trash2, Check } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const NotificationCenter = ({ notifications: initialNotifications }) => {
  const [notifications, setNotifications] = useState(initialNotifications);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'error':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-6 h-6 text-yellow-500" />;
      case 'info':
        return <Info className="w-6 h-6 text-blue-500" />;
      default:
        return <Bell className="w-6 h-6 text-gray-500" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev?.map(notif =>
        notif?.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev?.map(notif => ({ ...notif, read: true }))
    );
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev?.filter(notif => notif?.id !== id));
  };

  const deleteAllRead = () => {
    setNotifications(prev => prev?.filter(notif => !notif?.read));
  };

  const filteredNotifications = notifications?.filter(notif => {
    if (filter === 'unread') return !notif?.read;
    if (filter === 'read') return notif?.read;
    return true;
  });

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  if (!notifications || notifications?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Keine Benachrichtigungen
        </h3>
        <p className="text-gray-600">
          Sie haben derzeit keine Benachrichtigungen
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Actions */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Bell className="w-6 h-6 text-gray-700" />
            <h2 className="text-xl font-bold text-gray-900">Benachrichtigungen</h2>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2.5 py-0.5">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Check className="w-4 h-4" />
                <span>Alle als gelesen markieren</span>
              </button>
            )}
            {notifications?.some(n => n?.read) && (
              <button
                onClick={deleteAllRead}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Gelesene löschen</span>
              </button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-2">
          {[
            { id: 'all', label: 'Alle', count: notifications?.length },
            { id: 'unread', label: 'Ungelesen', count: unreadCount },
            { id: 'read', label: 'Gelesen', count: notifications?.length - unreadCount }
          ]?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setFilter(tab?.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === tab?.id
                  ? 'bg-blue-600 text-white' :'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab?.label} ({tab?.count})
            </button>
          ))}
        </div>
      </div>
      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications?.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-600">Keine Benachrichtigungen in dieser Kategorie</p>
          </div>
        ) : (
          filteredNotifications?.map(notification => (
            <div
              key={notification?.id}
              className={`bg-white rounded-lg shadow-sm border overflow-hidden transition-all ${
                !notification?.read ? 'border-l-4 border-l-blue-600' : ''
              }`}
            >
              <div className={`p-4 ${getNotificationColor(notification?.type)}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification?.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-gray-900 mb-1">
                          {notification?.title}
                        </h3>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {notification?.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {format(new Date(notification?.timestamp), 'dd. MMMM yyyy, HH:mm', { locale: de })} Uhr
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        {!notification?.read && (
                          <button
                            onClick={() => markAsRead(notification?.id)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Als gelesen markieren"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification?.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Löschen"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;